import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../bridge.css";
import { LOGO_URL } from "../lib/calc";
import VisionBoard from "./bridge/VisionBoard";
import GratitudeJournal from "./bridge/GratitudeJournal";
import BridgeAffirmations from "./bridge/BridgeAffirmations";
import useRouteMeta from "../lib/useRouteMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "fl_bridge_session";

function readSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj?.token || !obj?.email) return null;
    return obj;
  } catch (_e) { return null; }
}

function BridgeTopbar({ session, onLogout }) {
  return (
    <header className="bridge-topbar" data-testid="bridge-topbar">
      <a href="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
        <img src={LOGO_URL} alt="Finlightenment" />
      </a>
      <div className="bridge-topbar-tag">
        <span className="brand">Finlightenment<span className="reg">®</span></span>
        Bridge to <em>Becoming</em><span className="tm">™</span>
      </div>
      {session && (
        <div className="bridge-topbar-user">
          <span data-testid="bridge-user-email">{session.email}</span>
          <button onClick={onLogout} data-testid="bridge-logout">Sign out</button>
        </div>
      )}
    </header>
  );
}

function BridgeGate({ onSent }) {
  const [form, setForm] = useState({ first: "", last: "", email: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined, _api: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!form.first.trim()) er.first = "Please enter your first name.";
    if (!form.last.trim()) er.last = "Please enter your last name.";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid email address.";
    setErrors(er);
    if (Object.keys(er).length || submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bridge/auth/request-link`, {
        name: `${form.first.trim()} ${form.last.trim()}`,
        email: form.email.trim().toLowerCase(),
      });
      setSuccess(res.data?.message || "Check your inbox for a sign-in link.");
      onSent && onSent({ email: form.email.trim().toLowerCase(), name: `${form.first.trim()} ${form.last.trim()}` });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      setErrors({ _api: typeof msg === "string" ? msg : "Could not send link." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bridge-gate" data-testid="bridge-gate">
      <form className="bridge-gate-card" onSubmit={submit} noValidate>
        <span className="bridge-eyebrow">✦ Finlightenment · Bridge to Becoming</span>
        <h1 className="bridge-gate-title">Design the life you <em>deeply desire</em></h1>
        <p className="bridge-gate-sub">A sacred creative space to visualise your dream life, cultivate daily gratitude rooted in science, and write affirmations that truly bridge where you are with where you're going.</p>
        <div className="bridge-pillars">
          <span className="bridge-pillar">✦ Vision Board</span>
          <span className="bridge-pillar">✦ Gratitude Journal</span>
          <span className="bridge-pillar">✦ Bridge Affirmations</span>
        </div>

        {success ? (
          <div className="bridge-success-banner" data-testid="bridge-magic-sent">
            ✦ {success}
            <div style={{ fontSize: 12, color: "var(--bridge-muted)", marginTop: 8 }}>
              Tip: check your spam/promotions folder if you don't see it within 60 seconds.
            </div>
          </div>
        ) : (
          <>
            <div className="bridge-input-row">
              <input type="text" className={`bridge-input ${errors.first ? "error" : ""}`}
                placeholder="First name" value={form.first} onChange={update("first")}
                autoComplete="given-name" data-testid="bridge-first-name" />
              <input type="text" className={`bridge-input ${errors.last ? "error" : ""}`}
                placeholder="Last name" value={form.last} onChange={update("last")}
                autoComplete="family-name" data-testid="bridge-last-name" />
            </div>
            <input type="email" className={`bridge-input ${errors.email ? "error" : ""}`}
              placeholder="Your best email address" value={form.email} onChange={update("email")}
              autoComplete="email" style={{ marginBottom: 6 }} data-testid="bridge-email" />
            <div className="bridge-form-error" data-testid="bridge-form-error">
              {errors.first || errors.last || errors.email || errors._api || ""}
            </div>
            <button type="submit" className="bridge-btn" disabled={submitting} data-testid="bridge-submit">
              {submitting ? "Sending your sign-in link…" : "Enter my Bridge to Becoming →"}
            </button>
            <p className="bridge-privacy">🔒 We'll email you a one-time sign-in link. No passwords, no spam — ever.</p>
          </>
        )}
      </form>
    </div>
  );
}

export default function BridgeToBecoming() {
  useRouteMeta("bridge");
  const [session, setSession] = useState(readSession);
  const [tab, setTab] = useState("board");

  const handleLogout = () => {
    localStorage.removeItem(SESSION_KEY);
    setSession(null);
  };

  // Pre-configure axios with auth header for child components
  useEffect(() => {
    if (session?.token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${session.token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [session]);

  if (!session) {
    return (
      <div className="bridge-app">
        <BridgeTopbar />
        <BridgeGate />
      </div>
    );
  }

  return (
    <div className="bridge-app" data-testid="bridge-app">
      <BridgeTopbar session={session} onLogout={handleLogout} />
      <div className="bridge-container">
        <section className="bridge-welcome" data-testid="bridge-welcome">
          <p className="bridge-welcome-eyebrow">✦ Welcome to Bridge to Becoming</p>
          <h2>Your <em>financial transformation</em> begins in your nervous system — not your spreadsheet.</h2>
          <p>Hi {session.name?.split(" ")[0] || "friend"} — most people think this kind of work is just "mindset stuff" or general happiness practice. It isn't. <strong>Decades of behavioural finance research is unambiguous:</strong> the people who build lasting wealth are not the ones with the most income or the cleverest budget — they are the ones whose internal relationship with money allows them to <em>feel safe receiving it</em>, <em>trust themselves with it</em>, and <em>act intentionally over time</em>. That's exactly what these three tools are designed to build, in the order most people need them.</p>
          <p>This space is yours. There's no streak you have to keep, no wrong way to use it. But the people who lean in tend to look back six months later and notice that something fundamental in how they relate to money has quietly changed.</p>
          <div className="bridge-welcome-tabs">
            <div className="bridge-welcome-tab">
              <div className="bridge-welcome-tab-title">✦ Vision Board</div>
              <p className="bridge-welcome-tab-desc"><strong>Start here.</strong> Search free images and words, drag them into a board you can print and post somewhere visible. This trains your brain to recognise the future you're moving toward — the foundation of every other practice.</p>
            </div>
            <div className="bridge-welcome-tab">
              <div className="bridge-welcome-tab-title">✦ Gratitude Journal</div>
              <p className="bridge-welcome-tab-desc"><strong>Three minutes nightly.</strong> Three things you're grateful for — paired with the specific actions, choices, or openness that allowed each one in. This builds the agency thread that connects intentional living to financial outcomes.</p>
            </div>
            <div className="bridge-welcome-tab">
              <div className="bridge-welcome-tab-title">✦ Bridge Affirmations</div>
              <p className="bridge-welcome-tab-desc"><strong>Use weekly or as needed.</strong> Build affirmations that <em>actually work</em> by honouring where you came from, where you are, and where you're going — without triggering the resistance traditional affirmations usually create.</p>
            </div>
          </div>
        </section>

        <div className="bridge-tab-bar" role="tablist">
          <button data-testid="bridge-tab-board"
            className={`bridge-tab-btn ${tab === "board" ? "active" : ""}`}
            onClick={() => setTab("board")}>✦ Vision Board</button>
          <button data-testid="bridge-tab-gratitude"
            className={`bridge-tab-btn ${tab === "gratitude" ? "active" : ""}`}
            onClick={() => setTab("gratitude")}>✦ Gratitude Journal</button>
          <button data-testid="bridge-tab-affirmations"
            className={`bridge-tab-btn ${tab === "affirmations" ? "active" : ""}`}
            onClick={() => setTab("affirmations")}>✦ Bridge Affirmations</button>
        </div>

        {tab === "board" && <VisionBoard />}
        {tab === "gratitude" && <GratitudeJournal session={session} />}
        {tab === "affirmations" && <BridgeAffirmations />}
      </div>
    </div>
  );
}

// Verify route — invoked when user clicks the magic link in email
export function BridgeVerify() {
  useRouteMeta("bridge");
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying | error
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    const url = new URL(window.location.href);
    const token = url.searchParams.get("token");
    if (!token) {
      setStatus("error");
      setErrMsg("No token provided.");
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/bridge/auth/verify`, { params: { token } });
        const session = { token: res.data.token, email: res.data.email, name: res.data.name };
        localStorage.setItem(SESSION_KEY, JSON.stringify(session));
        navigate("/bridge", { replace: true });
      } catch (err) {
        setStatus("error");
        setErrMsg(err?.response?.data?.detail || "This sign-in link is invalid or expired.");
      }
    })();
  }, [navigate]);

  return (
    <div className="bridge-app">
      <BridgeTopbar />
      <div className="bridge-verify">
        <div className="bridge-verify-card" data-testid="bridge-verify">
          <div style={{ fontSize: 36, color: "var(--bridge-gold-deep)", marginBottom: 8 }}>✦</div>
          {status === "verifying" ? (
            <>
              <h2 style={{ margin: "0 0 8px", color: "var(--bridge-sage)" }}>Opening your space…</h2>
              <p style={{ color: "var(--bridge-muted)", fontSize: 14, margin: 0 }}>One moment while we verify your sign-in link.</p>
            </>
          ) : (
            <>
              <h2 style={{ margin: "0 0 8px", color: "var(--bridge-blush)" }}>Hmm — that link didn't work</h2>
              <p style={{ color: "var(--bridge-muted)", fontSize: 14, margin: "0 0 18px" }}>{errMsg}</p>
              <a href="/bridge" className="bridge-btn" style={{ width: "auto", padding: "12px 22px", textDecoration: "none" }}>
                Request a fresh link →
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
